import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';
import { usePaymentRequests, useUpdatePaymentRequest } from '@/hooks/usePaymentRequests';
import { useAuthors } from '@/hooks/useAuthors';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate } from '@/utils/formatters';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';

const AdminPayments: React.FC = () => {
  const { data: paymentRequests, isLoading } = usePaymentRequests();
  const { data: authors } = useAuthors();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [authorFilter, setAuthorFilter] = useState<string>('all');
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [noteText, setNoteText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const updatePaymentRequest = useUpdatePaymentRequest();

  // Format payment method safely
  const formatPaymentMethod = (method: string | undefined): string => {
    if (!method) return 'N/A';
    return method.replace(/_/g, ' ');
  };

  // Extract unique statuses for filter
  const statuses = React.useMemo(() => {
    if (!paymentRequests) return [];
    
    const uniqueStatuses = new Set(paymentRequests.map(request => request.status));
    return Array.from(uniqueStatuses);
  }, [paymentRequests]);

  // Apply filters
  const filteredRequests = React.useMemo(() => {
    if (!paymentRequests) return [];
    
    return paymentRequests.filter(request => {
      if (statusFilter !== 'all' && request.status !== statusFilter) {
        return false;
      }
      
      if (authorFilter !== 'all' && request.authorId !== authorFilter) {
        return false;
      }
      
      return true;
    });
  }, [paymentRequests, statusFilter, authorFilter]);

  const handleOpenActionDialog = (request: any, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setNoteText('');
    setDialogOpen(true);
  };

  const handleAction = async () => {
    if (!selectedRequest) return;
    
    const status = actionType === 'approve' ? 'approved' : 'rejected';
    const defaultNote = actionType === 'approve' 
      ? 'Payment request approved by admin'
      : 'Payment request rejected by admin';
    
    await updatePaymentRequest.mutateAsync({
      id: selectedRequest.id,
      status: status as any,
      notes: noteText || defaultNote
    });
    
    setDialogOpen(false);
  };

  const handlePaid = async (requestId: string) => {
    await updatePaymentRequest.mutateAsync({
      id: requestId,
      status: 'paid'
      // Don't update notes - preserve the approval notes
    });
  };

  return (
    <MainLayout allowedRoles={['admin']}>
      <PageHeader
        title="Payment Requests"
        description="Manage author payment requests"
      />
      
      <div className="mb-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <CardTitle>All Payment Requests</CardTitle>
              
              <div className="flex flex-wrap gap-2">
                <Select 
                  value={statusFilter} 
                  onValueChange={setStatusFilter}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statuses.map(status => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={authorFilter} 
                  onValueChange={setAuthorFilter}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Author" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Authors</SelectItem>
                    {authors?.map(author => (
                      <SelectItem key={author.id} value={author.id}>
                        {author.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center p-8">
                <p>Loading payment requests...</p>
              </div>
            ) : filteredRequests.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Author</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map(request => {
                    const author = authors?.find(a => a.id === request.authorId);
                    return (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">
                          {author?.name || 'Unknown'}
                        </TableCell>
                        <TableCell>{formatDate(request.requestDate)}</TableCell>
                        <TableCell className="capitalize">
                          {formatPaymentMethod(request.paymentMethod)}
                        </TableCell>
                        <TableCell className="text-right">
                          ${parseFloat(String(request.amount)).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            request.status === 'paid' ? 'default' : 
                            request.status === 'approved' ? 'outline' :
                            request.status === 'rejected' ? 'destructive' : 
                            'secondary'
                          }>
                            {request.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {request.notes}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {request.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleOpenActionDialog(request, 'approve')}
                                  title="Approve"
                                >
                                  <Check className="h-4 w-4 text-green-500" />
                                </Button>
                                <Button
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleOpenActionDialog(request, 'reject')}
                                  title="Reject"
                                >
                                  <X className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {request.status === 'approved' && (
                              <Button
                                variant="outline" 
                                size="sm"
                                onClick={() => handlePaid(request.id)}
                              >
                                Mark as Paid
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No payment requests found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve' : 'Reject'} Payment Request
            </DialogTitle>
            <DialogDescription>
              {actionType === 'approve' 
                ? 'Add a note for this approval (optional).'
                : 'Please provide a reason for rejecting this payment request.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder={
                actionType === 'approve' 
                  ? 'Enter approval note (optional)...'
                  : 'Enter reason for rejection...'
              }
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant={actionType === 'approve' ? 'default' : 'destructive'} 
              onClick={handleAction}
            >
              {actionType === 'approve' ? 'Approve' : 'Reject'} Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default AdminPayments;
